import type { BulkRegisterPreviewItem, HoldingCsvItem } from "@yutai/shared"
import { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import styles from "./BulkRegisterModal.module.css"
import { decodeRakutenCsvFile, parseRakutenHoldingsCsv } from "./rakutenCsv.js"

type Step = "input" | "preview"
type Mode = "codes" | "csv"

interface Props {
  open: boolean
  listName: string
  onClose: () => void
  onValidate: (codes: string[]) => Promise<BulkRegisterPreviewItem[]>
  onRegister: (companyIds: string[]) => Promise<number>
  onRegisterHoldings: (
    items: { companyId: string; acquisitionShares: number; acquisitionPrice: number }[]
  ) => Promise<number>
}

function reasonLabel(reason: BulkRegisterPreviewItem["reason"]): string {
  if (reason === "no_benefit") return "優待なし"
  if (reason === "unknown") return "不明な証券コード"
  if (reason === "already_in_list") return "登録済み"
  return ""
}

// CSVインポートでは既に登録済みの銘柄も取得株数・取得単価の上書き対象として選択可能にする
// （通常コード入力モードでは従来通りスキップのみ）。
function isActionable(item: BulkRegisterPreviewItem, mode: Mode): boolean {
  if (item.canRegister) return true
  return mode === "csv" && item.reason === "already_in_list"
}

function defaultSelectedCodes(items: BulkRegisterPreviewItem[], mode: Mode): Set<string> {
  return new Set(items.filter((p) => isActionable(p, mode)).map((p) => p.code))
}

export function BulkRegisterModal({
  open,
  listName,
  onClose,
  onValidate,
  onRegister,
  onRegisterHoldings,
}: Props) {
  const [mode, setMode] = useState<Mode>("codes")
  const [step, setStep] = useState<Step>("input")
  const [inputText, setInputText] = useState("")
  const [csvItems, setCsvItems] = useState<HoldingCsvItem[]>([])
  const [csvFileName, setCsvFileName] = useState<string | null>(null)
  const [preview, setPreview] = useState<BulkRegisterPreviewItem[]>([])
  const [selectedCodes, setSelectedCodes] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    if (!open) return
    document.body.style.overflow = "hidden"
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") onCloseRef.current()
    }
    document.addEventListener("keydown", onKeyDown)
    return () => {
      document.body.style.overflow = ""
      document.removeEventListener("keydown", onKeyDown)
    }
  }, [open])

  if (!open) return null

  function reset() {
    setMode("codes")
    setStep("input")
    setInputText("")
    setCsvItems([])
    setCsvFileName(null)
    setPreview([])
    setSelectedCodes(new Set())
    setError(null)
    setLoading(false)
  }

  function handleClose() {
    reset()
    onClose()
  }

  function switchMode(next: Mode) {
    setMode(next)
    setError(null)
  }

  async function handleCsvFile(file: File) {
    setError(null)
    setCsvItems([])
    setCsvFileName(null)
    try {
      const text = await decodeRakutenCsvFile(file)
      const items = parseRakutenHoldingsCsv(text)
      if (items.length === 0) {
        setError(
          "CSVから保有銘柄を読み取れませんでした。楽天証券の「資産状況」からダウンロードしたCSVファイルを選択してください。"
        )
        return
      }
      setCsvItems(items)
      setCsvFileName(file.name)
    } catch {
      setError("CSVの読み込みに失敗しました。")
    }
  }

  async function handleNext() {
    setLoading(true)
    setError(null)
    try {
      if (mode === "codes") {
        const lines = inputText
          .split(/\r?\n/)
          .map((l) => l.trim())
          .filter(Boolean)
        if (lines.length === 0) {
          setError("証券コードを入力してください")
          return
        }
        const results = await onValidate(lines)
        setPreview(results)
        setSelectedCodes(defaultSelectedCodes(results, mode))
      } else {
        if (csvItems.length === 0) {
          setError("CSVファイルを選択してください")
          return
        }
        const holdingByCode = new Map(csvItems.map((i) => [i.code, i]))
        const results = await onValidate(csvItems.map((i) => i.code))
        const merged = results.map((r) => {
          const holding = holdingByCode.get(r.code)
          return holding
            ? {
                ...r,
                acquisitionShares: holding.shares,
                acquisitionPrice: holding.acquisitionPrice,
              }
            : r
        })
        setPreview(merged)
        setSelectedCodes(defaultSelectedCodes(merged, mode))
      }
      setStep("preview")
    } catch {
      setError("確認に失敗しました。しばらくしてから再度お試しください。")
    } finally {
      setLoading(false)
    }
  }

  function toggleSelected(code: string, checked: boolean) {
    setSelectedCodes((prev) => {
      const next = new Set(prev)
      if (checked) next.add(code)
      else next.delete(code)
      return next
    })
  }

  async function handleRegister() {
    const toRegister = preview.filter((p) => isActionable(p, mode) && selectedCodes.has(p.code))
    if (toRegister.length === 0) return
    setLoading(true)
    setError(null)
    try {
      if (mode === "codes") {
        await onRegister(toRegister.map((p) => p.code))
      } else {
        await onRegisterHoldings(
          toRegister.map((p) => ({
            companyId: p.code,
            acquisitionShares: p.acquisitionShares ?? 0,
            acquisitionPrice: p.acquisitionPrice ?? 0,
          }))
        )
      }
      handleClose()
    } catch {
      setError("登録に失敗しました。しばらくしてから再度お試しください。")
      setLoading(false)
    }
  }

  const registrableCount = preview.filter((p) => isActionable(p, mode)).length
  const selectedCount = preview.filter(
    (p) => isActionable(p, mode) && selectedCodes.has(p.code)
  ).length

  return createPortal(
    <div className={styles.root}>
      <button type="button" className={styles.backdrop} aria-label="閉じる" onClick={handleClose} />
      <div
        className={styles.dialog}
        // biome-ignore lint/a11y/useSemanticElements: native <dialog> lacks the backdrop/animation control this needs
        role="dialog"
        aria-modal="true"
        aria-labelledby="bulk-register-title"
      >
        <div className={styles.header}>
          <h2 id="bulk-register-title" className={styles.title}>
            一括銘柄登録
          </h2>
          <button
            type="button"
            className={styles.closeBtn}
            aria-label="閉じる"
            onClick={handleClose}
          >
            ×
          </button>
        </div>

        <div className={styles.body}>
          {step === "input" && (
            <>
              <div className={styles.modeTabs}>
                <button
                  type="button"
                  className={mode === "codes" ? styles.modeTabActive : styles.modeTab}
                  onClick={() => switchMode("codes")}
                >
                  証券コードで入力
                </button>
                <button
                  type="button"
                  className={mode === "csv" ? styles.modeTabActive : styles.modeTab}
                  onClick={() => switchMode("csv")}
                >
                  証券会社CSVを取り込む
                </button>
              </div>

              {error && <p className={styles.error}>{error}</p>}

              {mode === "codes" && (
                <>
                  <p className={styles.hint}>
                    「{listName}」に追加する証券コードを改行区切りで入力してください（4桁）。
                  </p>
                  <textarea
                    className={styles.textarea}
                    placeholder="7203&#10;9984&#10;6758"
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    aria-label="証券コード"
                  />
                </>
              )}

              {mode === "csv" && (
                <>
                  <p className={styles.hint}>
                    楽天証券の「資産状況」からダウンロードしたCSVファイル（assetbalanceJP_*.csv）を選択してください。銘柄コード・保有株数・平均取得単価を読み取って「
                    {listName}
                    」に保有銘柄モードで登録します（現在値・評価損益などの相場情報は取り込みません）。
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,text/csv"
                    className={styles.fileInput}
                    onChange={(e) => {
                      const file = e.target.files?.[0]
                      if (file) handleCsvFile(file)
                    }}
                    aria-label="CSVファイル"
                  />
                  {csvFileName && csvItems.length > 0 && (
                    <p className={styles.csvSummary}>
                      {csvFileName} から {csvItems.length}銘柄を読み取りました。
                    </p>
                  )}
                </>
              )}
            </>
          )}

          {step === "preview" && (
            <>
              <p className={styles.summary}>
                選択中 {selectedCount}件 / 登録可能 {registrableCount}件 / 全 {preview.length}件
              </p>
              {error && <p className={styles.error}>{error}</p>}
              <ul className={styles.previewList}>
                {preview.map((item, index) => {
                  const actionable = isActionable(item, mode)
                  const isUpdate = mode === "csv" && !item.canRegister && actionable
                  return (
                    <li key={`${item.code}-${index}`} className={styles.previewRow}>
                      {actionable ? (
                        <label className={styles.previewCheck}>
                          <input
                            type="checkbox"
                            checked={selectedCodes.has(item.code)}
                            onChange={(e) => toggleSelected(item.code, e.target.checked)}
                          />
                        </label>
                      ) : (
                        <span className={styles.previewCheckSpacer} aria-hidden="true" />
                      )}
                      <span className={styles.previewMain}>
                        <span className={styles.previewNameRow}>
                          <span className={styles.previewCode}>{item.code}</span>
                          <span className={styles.previewName}>{item.name ?? "—"}</span>
                        </span>
                        {mode === "csv" && item.acquisitionShares != null && (
                          <span className={styles.previewHoldingInfo}>
                            {item.acquisitionShares.toLocaleString()}株 ・ 平均取得単価{" "}
                            {item.acquisitionPrice?.toLocaleString()}円
                          </span>
                        )}
                      </span>
                      {item.canRegister ? (
                        <span className={styles.badgeOk}>登録可能</span>
                      ) : isUpdate ? (
                        <span className={styles.badgeOk}>取得情報を更新</span>
                      ) : (
                        <span
                          className={
                            item.reason === "already_in_list" ? styles.badgeMuted : styles.badgeNo
                          }
                        >
                          {reasonLabel(item.reason)}
                        </span>
                      )}
                    </li>
                  )
                })}
              </ul>
            </>
          )}
        </div>

        <div className={styles.footer}>
          {step === "input" && (
            <button
              type="button"
              className={styles.primaryBtn}
              disabled={loading || (mode === "csv" && csvItems.length === 0)}
              onClick={handleNext}
            >
              {loading ? "確認中..." : "次へ"}
            </button>
          )}
          {step === "preview" && (
            <>
              <button
                type="button"
                className={styles.primaryBtn}
                disabled={loading || selectedCount === 0}
                onClick={handleRegister}
              >
                {loading ? "登録中..." : `${selectedCount}件を登録`}
              </button>
              <button
                type="button"
                className={styles.secondaryBtn}
                disabled={loading}
                onClick={() => {
                  setStep("input")
                  setError(null)
                }}
              >
                戻る
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}
